import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Play,
  Pause,
  Plus,
  X,
  Calendar as CalendarIcon,
  FileText,
  AlertCircle,
  Clock,
  Tag,
  Info,
  Users,
  Shield,
  ExternalLink,
} from 'lucide-react';
import type { Task } from '@/types/task';
import { format } from 'date-fns';
import { TaskDetailLinkedEntities } from './TaskDetailLinkedEntities';

interface TaskDetailOverviewProps {
  readonly task: Task;
  readonly onTaskUpdate: (task: Task) => void;
  readonly isEditMode?: boolean;
}

interface TimeEntry {
  id: string;
  date: string;
  duration: string;
  user: string;
}

interface AssigneeOption {
  id: string;
  name: string;
  email: string;
  type: 'user' | 'group';
}

const ASSIGNEE_OPTIONS: Record<string, AssigneeOption> = {
  'user-1': { id: 'user-1', name: 'John Smith', email: 'john.smith@example.com', type: 'user' },
  'user-2': { id: 'user-2', name: 'Jane Doe', email: 'jane.doe@example.com', type: 'user' },
  'user-3': { id: 'user-3', name: 'Bob Johnson', email: 'bob.johnson@example.com', type: 'user' },
  'group-1': { id: 'group-1', name: 'Engineering Team', email: 'team@example.com', type: 'group' },
};

function getAssigneeOption(value: string): AssigneeOption | undefined {
  return ASSIGNEE_OPTIONS[value];
}

function getStatusDotColor(status: string): string {
  switch (status) {
    case 'New':
      return 'bg-gray-400';
    case 'In Progress':
      return 'bg-blue-500';
    case 'Blocked':
      return 'bg-red-500';
    case 'Resolved':
      return 'bg-green-500';
    case 'Closed':
      return 'bg-gray-600';
    default:
      return 'bg-gray-400';
  }
}

function getPriorityDotColor(priority: string): string {
  switch (priority) {
    case 'Urgent':
      return 'bg-red-500';
    case 'High':
      return 'bg-orange-500';
    case 'Medium':
      return 'bg-yellow-500';
    case 'Low':
      return 'bg-gray-400';
    default:
      return 'bg-gray-400';
  }
}

export function TaskDetailOverview({ task, onTaskUpdate, isEditMode = false }: Readonly<TaskDetailOverviewProps>) {
  const [title, setTitle] = useState(task.title);
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState(task.description ?? '');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timeEntry, setTimeEntry] = useState('');
  const [newTag, setNewTag] = useState('');
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([
    { id: '1', date: 'Today, 10:30 AM', duration: '2h 15m', user: 'Current User' },
    { id: '2', date: 'Yesterday, 2:00 PM', duration: '1h 30m', user: 'Current User' },
  ]);

  // Check if task has compliance context
  const complianceLink = task.links?.find(link => link.type === 'asset' && link.metadata?.complianceId);
  const hasComplianceContext = !!complianceLink;

  // Sync local state with task prop changes
  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description ?? '');
  }, [task.title, task.description]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    
    // Validation
    if (!value.trim()) {
      setTitleError('Title is required');
    } else if (value.length > 200) {
      setTitleError('Title must be 200 characters or less');
    } else {
      setTitleError('');
      onTaskUpdate({ ...task, title: value });
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    onTaskUpdate({ ...task, description: value });
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      const updatedTags = [...(task.tags ?? []), newTag.trim()];
      onTaskUpdate({ ...task, tags: updatedTags });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = (task.tags ?? []).filter(tag => tag !== tagToRemove);
    onTaskUpdate({ ...task, tags: updatedTags });
  };

  const handleAddTimeEntry = () => {
    if (timeEntry.trim()) {
      const newEntry: TimeEntry = {
        id: Date.now().toString(),
        date: 'Today, ' + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        duration: timeEntry,
        user: 'Current User',
      };
      setTimeEntries([newEntry, ...timeEntries]);
      setTimeEntry('');
    }
  };

  const handleRemoveTimeEntry = (entryId: string) => {
    setTimeEntries(timeEntries.filter(entry => entry.id !== entryId));
  };



  return (
    <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-6">
      {/* Left Column - Main Content */}
      <div className="space-y-6">
        {/* Work Item Details (Title & Description) */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Work Item Details
            </CardTitle>
            <CardDescription className="text-xs">
              Work item title and detailed information about requirements and objectives
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Title <span className="text-destructive">*</span>
              </label>
              {isEditMode ? (
                <div className="space-y-1">
                  <Input
                    value={title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTitleChange(e.target.value)}
                    placeholder="Enter task title..."
                    className={`bg-white ${titleError ? 'border-destructive' : ''}`}
                    maxLength={200}
                  />
                  {titleError && (
                    <p className="text-xs text-destructive">{titleError}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {title.length}/200 characters
                  </p>
                </div>
              ) : (
                <Input
                  value={title}
                  readOnly
                  className="bg-muted"
                />
              )}
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              {isEditMode ? (
                <Textarea
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleDescriptionChange(e.target.value)}
                  placeholder="Add a description..."
                  className="min-h-[200px] resize-none bg-white"
                />
              ) : (
                <Textarea
                  value={description || 'No description provided'}
                  readOnly
                  className="min-h-[200px] resize-none bg-muted"
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compliance Context Card - Only show if task is linked to compliance */}
        {hasComplianceContext && complianceLink && (
          <Card className="border border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Shield className="h-5 w-5" />
                Compliance Context
              </CardTitle>
              <CardDescription className="text-xs text-blue-700">
                This task is linked to a compliance requirement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-blue-700 mb-1">Compliance Item</div>
                  <div className="text-sm font-medium text-blue-900">
                    {complianceLink.metadata?.complianceName || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-blue-700 mb-1">Regulation Type</div>
                  <div className="text-sm font-medium text-blue-900">
                    {complianceLink.metadata?.regulationType || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-blue-700 mb-1">Asset</div>
                  <div className="text-sm font-medium text-blue-900">
                    {complianceLink.entityName}
                    {complianceLink.metadata?.assetCode && (
                      <span className="text-xs text-blue-600 ml-1">
                        ({complianceLink.metadata.assetCode})
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-blue-700 mb-1">Actions</div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-blue-300 hover:bg-blue-100"
                    onClick={() => {
                      // Navigate to compliance item
                      window.location.href = `/compliance-assets?highlight=${complianceLink.metadata?.complianceId}`;
                    }}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View Compliance
                  </Button>
                </div>
              </div>
              <Alert variant="info" showIcon={false} className="bg-blue-100/50 border-blue-200 mt-3">
                <div className="text-xs text-blue-800">
                  <strong>Note:</strong> Completing this task may update the compliance status. 
                  Ensure all required actions are documented.
                </div>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* Status, Priority, Type, and Due Date - Two Cards Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Status & Priority Card */}
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertCircle className="h-4 w-4" />
                Status & Priority
              </CardTitle>
              <CardDescription className="text-xs">
                Current task state and urgency level
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                {isEditMode ? (
                  <Select 
                    value={task.status} 
                    onValueChange={(value: string) => onTaskUpdate({ ...task, status: value as Task['status'] })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-gray-400" />
                          <span>New</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="In Progress">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                          <span>In Progress</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Blocked">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-red-500" />
                          <span>Blocked</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Resolved">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                          <span>Resolved</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Closed">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-gray-600" />
                          <span>Closed</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                      <div className={`h-2 w-2 rounded-full ${getStatusDotColor(task.status)}`} />
                    </div>
                    <Input
                      value={task.status}
                      readOnly
                      className="bg-muted pl-8"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Priority</label>
                {isEditMode ? (
                  <Select 
                    value={task.priority} 
                    onValueChange={(value: string) => onTaskUpdate({ ...task, priority: value as Task['priority'] })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Urgent">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-red-500" />
                          <span>Urgent</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="High">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-orange-500" />
                          <span>High</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Medium">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-yellow-500" />
                          <span>Medium</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="Low">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-gray-400" />
                          <span>Low</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                      <div className={`h-2 w-2 rounded-full ${getPriorityDotColor(task.priority)}`} />
                    </div>
                    <Input
                      value={task.priority}
                      readOnly
                      className="bg-muted pl-8"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Type & Due Date Card */}
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarIcon className="h-4 w-4" />
                Type & Due Date
              </CardTitle>
              <CardDescription className="text-xs">
                Work item classification and deadline information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Type</label>
                {isEditMode ? (
                  <Select 
                    value={task.type} 
                    onValueChange={(value: string) => onTaskUpdate({ ...task, type: value as Task['type'] })}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General Task">General Task</SelectItem>
                      <SelectItem value="Inspection">Inspection</SelectItem>
                      <SelectItem value="Maintenance Request">Maintenance Request</SelectItem>
                      <SelectItem value="Documentation Update">Documentation Update</SelectItem>
                      <SelectItem value="Verification">Verification</SelectItem>
                      <SelectItem value="Follow-up Action">Follow-up Action</SelectItem>
                      <SelectItem value="Data Collection">Data Collection</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={task.type}
                    readOnly
                    className="bg-muted"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">Due Date</label>
                {isEditMode ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {task.dueDate ? (
                          format(new Date(task.dueDate), 'PPP')
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={task.dueDate ? new Date(task.dueDate) : undefined}
                        onSelect={(date) => onTaskUpdate({ ...task, dueDate: date?.toISOString() })}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      value={task.dueDate ? format(new Date(task.dueDate), 'PPP') : 'Not set'}
                      readOnly
                      className="bg-muted pl-10"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Time Tracking */}
        <Card className="border border-border/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <CardTitle>Time Tracking</CardTitle>
              </div>
              {isEditMode && (
                <div className="flex items-center gap-3">
                  {isTimerRunning && (
                    <span className="text-sm font-mono">00:15:23</span>
                  )}
                  <Button
                    variant={isTimerRunning ? 'destructive' : 'default'}
                    size="sm"
                    onClick={() => setIsTimerRunning(!isTimerRunning)}
                  >
                    {isTimerRunning ? (
                      <>
                        <Pause className="h-4 w-4 mr-2" />
                        Stop Timer
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Start Timer
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            <CardDescription className="text-xs">
              Track time spent on this task with manual entries or timer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Manual Time Entry */}
            {isEditMode && (
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">
                  Manual Entry (e.g., "2h 30m", "1.5h", "90m")
                </label>
                <div className="flex gap-2">
                  <Input
                    value={timeEntry}
                    onChange={(e) => setTimeEntry(e.target.value)}
                    placeholder="Enter time..."
                    className="flex-1"
                  />
                  <Button onClick={handleAddTimeEntry} size="sm">
                    Add
                  </Button>
                </div>
              </div>
            )}

            {/* Time Summary */}
            <Alert variant="info" showIcon={false} className="bg-blue-50/50 border-blue-100">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Logged:</span>
                  <span className="font-semibold">3h 45m</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Estimated:</span>
                  <span>8h</span>
                </div>
              </div>
            </Alert>

            {/* Recent Entries */}
            {timeEntries.length > 0 && (
              <div className="space-y-3 pt-4">
                <h4 className="text-sm font-medium text-muted-foreground">Recent Entries</h4>
                <div className="border rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-2.5 bg-muted/50 border-b text-xs font-medium text-muted-foreground">
                    <div>Date & Time</div>
                    <div className="text-right w-20">Duration</div>
                    <div className="w-28">User</div>
                    {isEditMode && <div className="w-8"></div>}
                  </div>
                  
                  {/* Table Body */}
                  <div className="divide-y">
                    {timeEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-4 py-3 hover:bg-muted/30 transition-colors group"
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-foreground">{entry.date}</span>
                        </div>
                        <div className="text-sm font-semibold text-right w-20 flex items-center justify-end">
                          {entry.duration}
                        </div>
                        <div className="text-sm text-muted-foreground w-28 flex items-center">
                          {entry.user}
                        </div>
                        {isEditMode && (
                          <div className="w-8 flex items-center justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleRemoveTimeEntry(entry.id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tags */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Tags
            </CardTitle>
            <CardDescription className="text-xs">
              Organise and categorise this task with custom labels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Existing Tags */}
              <div className="flex flex-wrap gap-2">
                {(task.tags ?? []).map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className={isEditMode ? "group hover:bg-destructive/10" : ""}
                  >
                    {tag}
                    {isEditMode && (
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </Badge>
                ))}
                {(!task.tags || task.tags.length === 0) && (
                  <span className="text-sm text-muted-foreground">No tags added</span>
                )}
              </div>

              {/* Add Tag */}
              {isEditMode && (
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Add a tag..."
                    className="flex-1"
                  />
                  <Button onClick={handleAddTag} size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Linked Entities */}
      <div className="space-y-6">
        {/* Work Item Details Card */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Work Item Information
            </CardTitle>
            <CardDescription className="text-xs">
              Key details and metadata about this task
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <div className="text-muted-foreground">Created by:</div>
              <div className="font-medium">{task.createdBy}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Created:</div>
              <div>{task.createdAt ? format(new Date(task.createdAt), 'dd/MM/yyyy, HH:mm:ss') : 'N/A'}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Last updated by:</div>
              <div className="font-medium">{task.updatedBy || task.createdBy}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Last updated:</div>
              <div>{task.updatedAt ? format(new Date(task.updatedAt), 'dd/MM/yyyy, HH:mm:ss') : 'N/A'}</div>
            </div>
          </CardContent>
        </Card>

        {/* Assigned To Card */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              Assigned To
            </CardTitle>
            <CardDescription className="text-xs">
              Person or team responsible for this task
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditMode ? (
              <Select
                value={task.assignee?.id ?? 'unassigned'}
                onValueChange={(value: string) => {
                  if (value === 'unassigned') {
                    onTaskUpdate({ ...task, assignee: undefined });
                  } else {
                    const selectedUser = getAssigneeOption(value);
                    if (selectedUser) {
                      onTaskUpdate({ ...task, assignee: selectedUser });
                    }
                  }
                }}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  <SelectItem value="user-1">John Smith</SelectItem>
                  <SelectItem value="user-2">Jane Doe</SelectItem>
                  <SelectItem value="user-3">Bob Johnson</SelectItem>
                  <SelectItem value="group-1">Engineering Team</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="space-y-2">
                <Input
                  value={task.assignee?.name ?? 'Unassigned'}
                  readOnly
                  className="bg-muted"
                />
                {task.assignee?.email && (
                  <div className="text-xs text-muted-foreground px-3">
                    {task.assignee.email}
                  </div>
                )}
                {task.assignee?.type === 'group' && (
                  <div className="text-xs text-muted-foreground px-3">
                    Group Assignment
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked Entities */}
        <TaskDetailLinkedEntities task={task} onTaskUpdate={onTaskUpdate} isEditMode={isEditMode} />
      </div>
    </div>
  );
}
